import { friendlyDbError } from '../utils/friendlyDbError';
import { create } from 'zustand';
import { supabase } from '../supabase';

export interface ClientStateChange {
  id: string;
  organization_id: string;
  client_id: string;
  from_state_id?: string;
  to_state_id: string;
  changed_by: string;
  changed_at: string;
  reason?: string;
  source: string;
  trigger_event?: string;
  metadata?: any;

  // Da client_state_changes_view
  client_name?: string;
  from_state_name?: string;
  from_state_color?: string;
  from_state_is_lead?: boolean;
  to_state_name?: string;
  to_state_color?: string;
  to_state_is_lead?: boolean;
  changed_by_name?: string;
  changed_by_email?: string;
}

interface ClientStateChangesStore {
  stateChanges: ClientStateChange[];
  loading: boolean;
  error: string | null;

  // Funzioni per recuperare i dati
  fetchStateChanges: (clientId?: string) => Promise<void>;
  fetchStateChangesForClient: (clientId: string) => Promise<void>;
  
  // Funzione per loggare un cambio di stato manualmente
  logStateChange: (params: {
    clientId: string;
    fromStateId?: string;
    toStateId: string;
    reason?: string;
    source?: string;
    triggerEvent?: string;
    metadata?: any;
  }) => Promise<ClientStateChange | null>;
  
  // Funzioni di utilità
  getClientStateHistory: (clientId: string) => ClientStateChange[];
  getStateTransitions: () => { from: string; to: string; count: number }[];
  
  // Reset
  clearStateChanges: () => void;
}

export const useClientStateChangesStore = create<ClientStateChangesStore>((set, get) => ({
  stateChanges: [],
  loading: false,
  error: null,

  fetchStateChanges: async (clientId) => {
    set({ loading: true, error: null });
    try {
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) throw new Error('User not authenticated');

      const { data: profileData } = await supabase
        .from('user_profiles')
        .select('organization_id')
        .eq('id', userData.user.id)
        .single();

      if (!profileData?.organization_id) throw new Error('Organization not found');

      let query = supabase
        .from('client_state_changes_view')
        .select('*')
        .eq('organization_id', profileData.organization_id)
        .order('changed_at', { ascending: false });

      // Se specificato un client, filtra per quel client
      if (clientId) {
        query = query.eq('client_id', clientId);
      }

      const { data, error } = await query.limit(100); // Limita a 100 record più recenti

      if (error) throw error;
      set({ stateChanges: data || [] });
    } catch (error) {
      set({ error: friendlyDbError(error) });
    } finally {
      set({ loading: false });
    }
  },

  fetchStateChangesForClient: async (clientId: string) => {
    return get().fetchStateChanges(clientId);
  },

  logStateChange: async ({
    clientId,
    fromStateId,
    toStateId,
    reason,
    source = 'manual',
    triggerEvent,
    metadata
  }) => {
    try {
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) throw new Error('User not authenticated');

      const { data: profileData } = await supabase
        .from('user_profiles')
        .select('organization_id')
        .eq('id', userData.user.id)
        .single();

      if (!profileData?.organization_id) throw new Error('Organization not found');

      const { data, error } = await supabase
        .from('client_state_changes')
        .insert({
          organization_id: profileData.organization_id,
          client_id: clientId,
          from_state_id: fromStateId,
          to_state_id: toStateId,
          changed_by: userData.user.id,
          reason,
          source,
          trigger_event: triggerEvent,
          metadata
        })
        .select()
        .single();

      if (error) throw error;

      // Aggiorna la lista locale
      const currentChanges = get().stateChanges;
      const updatedChanges = [data, ...currentChanges];
      set({ stateChanges: updatedChanges });

      return data;
    } catch (error) {
      set({ error: friendlyDbError(error) });
      return null;
    }
  },

  getClientStateHistory: (clientId: string) => {
    return get().stateChanges.filter(change => change.client_id === clientId);
  },

  getStateTransitions: () => {
    const stateChanges = get().stateChanges;
    const transitions: Record<string, number> = {};

    stateChanges.forEach(change => {
      const fromState = change.from_state_name || 'Initial';
      const toState = change.to_state_name || 'Unknown';
      const key = `${fromState} → ${toState}`;
      transitions[key] = (transitions[key] || 0) + 1;
    });

    return Object.entries(transitions).map(([transition, count]) => {
      const [from, to] = transition.split(' → ');
      return { from, to, count };
    });
  },

  clearStateChanges: () => {
    set({ stateChanges: [], error: null });
  }
}));